import React from 'react';
import { Link, Navigate, useSearchParams } from 'react-router-dom';
import { Loader2, Inbox, ChevronRight, UserPlus, Tag, Download } from 'lucide-react';
import type { SelfServiceSubmissionRow } from '../../types/selfService';
import { publisherStatusBadge } from '../../components/self-service/campaignSharedHelpers';
import { useAdminContext } from '../../components/self-service/admin/useAdminContext';
import { scopeForContext } from '../../components/self-service/admin/stationScopeRequest';
import AdminPageHeader from '../../components/self-service/admin/AdminPageHeader';
import { ALL_STATIONS_SLUG } from '../../components/self-service/admin/lastAdminStation';
import { useAuth } from '../../components/self-service/auth/useAuth';
import { rankOf } from '../../components/self-service/shared/roleRank';
import {
  isAwaitingAdvertiserFix,
  isSwapPending,
} from '../../components/self-service/submissionReviewPredicates';
import { apiClient } from '../../lib/apiClient';

type SubmissionRow = Pick<
  SelfServiceSubmissionRow,
  'id' | 'publisher_slug' | 'station_name' | 'contact_email' | 'business_info' | 'campaign'
  | 'creatives' | 'amount_cents' | 'service_fee_cents'
  | 'status' | 'stripe_payment_intent_id' | 'created_at' | 'reviewed_at'
  | 'referral_code' | 'promo_code'
>;

type TabKey = 'review' | 'approved' | 'other';

const TABS: { key: TabKey; label: string; matches: (s: SubmissionRow) => boolean }[] = [
  // CTV-732: the needsLibraryUpload term is gone from both tabs — a
  // library miss is super-admin work now, not a publisher review state.
  { key: 'review', label: 'Pending review', matches: (s) => s.status === 'payment_authorized' || isSwapPending(s) || isAwaitingAdvertiserFix(s) },
  { key: 'approved', label: 'Approved', matches: (s) => s.status === 'approved' && !isSwapPending(s) },
  { key: 'other', label: 'Other', matches: (s) => !['payment_authorized', 'approved', 'creative_rejected'].includes(s.status) },
];

const isTabKey = (v: string | null): v is TabKey =>
  v === 'review' || v === 'approved' || v === 'other';

const PublisherSubmissionsPage: React.FC = () => {
  const ctx = useAdminContext();
  const aggregated = ctx.mode === 'aggregated';
  // The scope's slug segment — the sentinel in aggregated mode.
  const scopeSlug = aggregated ? ALL_STATIONS_SLUG : ctx.slug;
  const scope = scopeForContext(ctx);
  const scopeKey = JSON.stringify(scope);
  const { user } = useAuth();
  // CTV-673 audit: submission review is manager+ — the API refuses
  // sales_rep on every submissions route, so don't render (or fetch).
  const allowed = rankOf(user?.rawRole) >= rankOf('manager');
  const [rows, setRows] = React.useState<SubmissionRow[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  // CTV-530: the Overview's "Needs your review" View-all link routes here
  // with ?tab=review so the publisher lands on the Pending Review tab.
  const initialTab = searchParams.get('tab');
  const [tab, setTab] = React.useState<TabKey>(isTabKey(initialTab) ? initialTab : 'review');
  const selectTab = (t: TabKey) => {
    setTab(t);
    setSearchParams({ tab: t });
  };

  const refresh = React.useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiClient.post<{ rows: SubmissionRow[] }>(
        '/submissions/list-by-publisher',
        JSON.parse(scopeKey),
      );
      setRows(data.rows ?? []);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load submissions');
    } finally {
      setLoading(false);
    }
  }, [scopeKey]);

  React.useEffect(() => {
    if (allowed) refresh();
  }, [refresh, allowed]);

  const counts = React.useMemo(
    () => TABS.reduce((acc, t) => ({ ...acc, [t.key]: rows.filter(t.matches).length }), {} as Record<TabKey, number>),
    [rows],
  );
  const filtered = React.useMemo(
    () => rows.filter(TABS.find((t) => t.key === tab)!.matches),
    [rows, tab],
  );

  if (!allowed) {
    return <Navigate to={`/admin/${scopeSlug}`} replace />;
  }

  return (
    <div className="max-w-5xl">
      <AdminPageHeader
        title="Submissions"
        scopeMode="flexible"
        currentSlug={scopeSlug}
        actions={
          <>
            <button
              type="button"
              onClick={() => exportSubmissionsCsv(rows, scopeSlug)}
              disabled={rows.length === 0}
              className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900 disabled:opacity-50"
            >
              <Download className="w-4 h-4" />
              Export CSV
            </button>
            <button onClick={refresh} className="text-sm text-slate-600 hover:text-slate-900">Refresh</button>
          </>
        }
      />

        <div className="border-b border-slate-200 mb-6">
          <nav className="flex gap-1">
            {TABS.map((t) => (
              <button
                key={t.key}
                onClick={() => selectTab(t.key)}
                className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                  tab === t.key
                    ? 'border-slate-900 text-slate-900'
                    : 'border-transparent text-slate-600 hover:text-slate-900'
                }`}
              >
                {t.label}
                <span className={`ml-2 text-xs px-1.5 py-0.5 rounded ${tab === t.key ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-600'}`}>
                  {counts[t.key]}
                </span>
              </button>
            ))}
          </nav>
        </div>

        {error && <p className="mb-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded p-3">{error}</p>}

        {loading ? (
          <div className="text-sm text-slate-500"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="bg-white border border-slate-200 rounded-xl p-10 text-center">
            <Inbox className="w-10 h-10 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-700 font-medium">
              {tab === 'review' ? 'No submissions waiting for review' : tab === 'approved' ? 'No approved campaigns yet' : 'Nothing here'}
            </p>
            <p className="text-sm text-slate-500 mt-1">
              {tab === 'review' ? 'New advertiser submissions will appear here for your review.' : ''}
            </p>
          </div>
        ) : tab === 'review' ? (
          <ReviewTabBody rows={filtered} showStation={aggregated} />
        ) : (
          <ul className="space-y-2">
            {filtered.map((s) => (
              <SubmissionRowItem key={s.id} s={s} showStation={aggregated} />
            ))}
          </ul>
        )}
    </div>
  );
};

// Pending tab body: split rows into two sections — Creative swaps (post-
// approval edits awaiting publisher action) above New submissions (initial
// payment_authorized rows). Empty groups are hidden. CTV-440.
function ReviewTabBody({
  rows, showStation,
}: {
  rows: SubmissionRow[];
  showStation: boolean;
}) {
  const swaps = rows.filter(isSwapPending);
  const awaitingFix = rows.filter((s) => isAwaitingAdvertiserFix(s) && !isSwapPending(s));
  const newSubmissions = rows.filter((s) => !isSwapPending(s) && !isAwaitingAdvertiserFix(s));
  return (
    <div className="space-y-6">
      {swaps.length > 0 && (
        <section>
          <h2 className="text-[11px] font-semibold uppercase tracking-[0.1em] text-slate-500 mb-2">
            Creative swaps · {swaps.length}
          </h2>
          <ul className="space-y-2">
            {swaps.map((s) => (
              <SubmissionRowItem key={s.id} s={s} showStation={showStation} swapPending />
            ))}
          </ul>
        </section>
      )}
      {newSubmissions.length > 0 && (
        <section>
          <h2 className="text-[11px] font-semibold uppercase tracking-[0.1em] text-slate-500 mb-2">
            New submissions · {newSubmissions.length}
          </h2>
          <ul className="space-y-2">
            {newSubmissions.map((s) => (
              <SubmissionRowItem key={s.id} s={s} showStation={showStation} />
            ))}
          </ul>
        </section>
      )}
      {awaitingFix.length > 0 && (
        <section>
          <h2 className="text-[11px] font-semibold uppercase tracking-[0.1em] text-slate-500 mb-2">
            Awaiting advertiser fix · {awaitingFix.length}
          </h2>
          <ul className="space-y-2">
            {awaitingFix.map((s) => (
              <SubmissionRowItem key={s.id} s={s} showStation={showStation} awaitingFix />
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

// CTV-759: the row's slug and product name come from the ROW, not the page.
// `selectedProductName` is resolved server-side, which deletes the old
// lookup against config.products (one station's catalogue, so it mislabelled
// rows in aggregated mode) and incidentally fixes deactivated products
// rendering as raw UUIDs. Clicking a row is a Scope collapse to its station.
function SubmissionRowItem({
  s, showStation, swapPending, awaitingFix,
}: {
  s: SubmissionRow;
  showStation: boolean;
  swapPending?: boolean;
  awaitingFix?: boolean;
}) {
  const total = (s.amount_cents / 100).toFixed(2);
  const productName = s.campaign?.selectedProductName ?? s.campaign?.selectedProduct ?? '—';
  return (
    <li>
      <Link
        to={`/admin/${s.publisher_slug}/submissions/${s.id}`}
        className="flex items-center justify-between gap-3 bg-white border border-slate-200 rounded-xl p-4 hover:border-slate-400 hover:shadow-sm transition-all"
      >
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <span className="text-base font-semibold text-slate-900 truncate">
              {s.business_info?.businessName || 'Advertiser'}
              {s.campaign?.campaignName && (
                <>
                  <span className="mx-2 text-slate-300 font-normal">|</span>
                  <span className="text-slate-700">{s.campaign.campaignName}</span>
                </>
              )}
            </span>
            <span className={`text-xs font-medium px-2 py-0.5 rounded ${publisherStatusBadge(s.status).className}`}>
              {publisherStatusBadge(s.status).label}
            </span>
            {showStation && (s.station_name || s.publisher_slug) && (
              <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 border border-slate-200">
                {s.station_name || s.publisher_slug}
              </span>
            )}
            {swapPending && (
              <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-amber-50 text-amber-800 border border-amber-200">
                Creative swap
              </span>
            )}
            {awaitingFix && (
              <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 border border-slate-200">
                Waiting on advertiser
              </span>
            )}
            {s.referral_code && (
              <span className="inline-flex items-center gap-1 text-[10px] font-mono px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 border border-blue-200">
                <UserPlus className="w-3 h-3" />
                {s.referral_code}
              </span>
            )}
            {s.promo_code && (
              <span className="inline-flex items-center gap-1 text-[10px] font-mono px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200">
                <Tag className="w-3 h-3" />
                {s.promo_code}
              </span>
            )}
          </div>
          <div className="text-xs text-slate-500">
            ${total} · {productName} · {new Date(s.created_at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
          </div>
        </div>
        <ChevronRight className="w-5 h-5 text-slate-400 flex-shrink-0" />
      </Link>
    </li>
  );
}

// CSV export covers every submission for the publisher. Includes referral
// + promo columns so the publisher can run their commissions/promo reports
// in Excel without needing a separate per-rep export.
function csvCell(v: string | number | null | undefined): string {
  const s = v == null ? '' : String(v);
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

function exportSubmissionsCsv(rows: SubmissionRow[], slug: string): void {
  const header = ['Submitted', 'Station', 'Business', 'Campaign', 'Status', 'Amount', 'Fee', 'Product', 'Start', 'End', 'Referral code', 'Promo code'];
  const fmtTs = (iso: string) => {
    const d = new Date(iso);
    return isNaN(d.getTime()) ? '' : d.toISOString().slice(0, 16).replace('T', ' ');
  };
  const lines = [
    header.map(csvCell).join(','),
    ...rows.map((r) => [
      fmtTs(r.created_at),
      r.station_name ?? r.publisher_slug ?? '',
      r.business_info?.businessName ?? '',
      r.campaign?.campaignName ?? '',
      r.status,
      (r.amount_cents / 100).toFixed(2),
      (r.service_fee_cents / 100).toFixed(2),
      r.campaign?.selectedProductName ?? r.campaign?.selectedProduct ?? '',
      r.campaign?.startDate ?? '',
      r.campaign?.endDate ?? '',
      r.referral_code ?? '',
      r.promo_code ?? '',
    ].map(csvCell).join(',')),
  ];
  const csv = lines.join('\r\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `submissions-${slug}-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export default PublisherSubmissionsPage;
